
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

const Profile = () => {
  const { user, logout } = useAuthStore();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    logout();
    setShowLogoutConfirm(false);
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Full system access and management capabilities';
      case 'MANAGER':
        return 'Vendor and customer management, order oversight';
      case 'SE':
        return 'Customer registration and order processing';
      default:
        return 'Standard user access';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800';
      case 'SE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Please log in to view your profile</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-[var(--color-primary)] mb-2">
          My Profile
        </h1>
        <p className="text-gray-600">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Profile Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-[var(--color-primary)] rounded-full flex items-center justify-center text-white text-2xl font-semibold mr-4">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-[var(--color-primary)]">
                  {user.name}
                </h2>
                <div className="flex items-center mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                  <p className="text-lg text-gray-900">{user.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
                  <p className="text-lg text-gray-900">{user.role}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Role Description</label>
                  <p className="text-gray-700">{getRoleDescription(user.role)}</p>
                </div>
              </div>

              {/* Account Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-[var(--color-primary)] mb-4">
                  Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Account Status</label>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-gray-900">Active</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Last Login</label>
                    <p className="text-gray-900">{new Date().toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
                    <p className="text-gray-900">{new Date().getFullYear()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Session Expires</label>
                    <p className="text-gray-900">24 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar with Actions and Preferences */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-[var(--color-primary)] mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center px-4 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors cursor-pointer">
                <i className="ri-settings-line w-5 h-5 flex items-center justify-center mr-3 text-[var(--color-primary)]"></i>
                Account Settings
              </button>
              <button className="w-full flex items-center px-4 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors cursor-pointer">
                <i className="ri-lock-line w-5 h-5 flex items-center justify-center mr-3 text-[var(--color-primary)]"></i>
                Change Password
              </button>
              <button className="w-full flex items-center px-4 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-md transition-colors cursor-pointer">
                <i className="ri-notification-line w-5 h-5 flex items-center justify-center mr-3 text-[var(--color-primary)]"></i>
                Notifications
              </button>
            </div>
          </div>

          {/* System Permissions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-[var(--color-primary)] mb-4">Permissions</h3>
            <div className="space-y-3">
              {user.role === 'ADMIN' && (
                <>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Full System Access
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    User Management
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Vendor Management
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Customer Management
                  </div>
                </>
              )}
              {user.role === 'MANAGER' && (
                <>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Vendor Management
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Customer Management
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Order Oversight
                  </div>
                </>
              )}
              {user.role === 'SE' && (
                <>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Customer Registration
                  </div>
                  <div className="flex items-center text-sm text-green-600">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Order Processing
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Logout Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-[var(--color-primary)] mb-4">Account Actions</h3>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line w-5 h-5 flex items-center justify-center mr-2"></i>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[var(--color-primary)] mb-4">
              Confirm Sign Out
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to sign out of your account?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors cursor-pointer"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
